import { createBrowserRouter, Outlet } from "react-router";
import { lazy, Suspense } from "react";
import { Root } from "./root";
import { PageSpinner } from "./shared/ui";

// Lazy loading для страниц
const HomePage = lazy(() =>
  import("./pages/home").then((m) => ({ default: m.HomePage }))
);
const LoginPage = lazy(() =>
  import("./pages/login").then((m) => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
  import("./pages/register").then((m) => ({ default: m.RegisterPage }))
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/forgot-password").then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const SetPasswordPage = lazy(() =>
  import("./pages/set-password").then((m) => ({ default: m.SetPasswordPage }))
);
const ProfilePage = lazy(() =>
  import("./pages/profile").then((m) => ({ default: m.ProfilePage }))
);
const CharacterPage = lazy(() =>
  import("./pages/character").then((m) => ({ default: m.CharacterPage }))
);
const PaymentPage = lazy(() =>
  import("./pages/payment").then((m) => ({ default: m.PaymentPage }))
);
const SimulatorPage = lazy(() =>
  import("./pages/simulator").then((m) => ({ default: m.SimulatorPage }))
);
const DevRealBlocksPage = lazy(() =>
  import("./pages/dev-real-blocks").then((m) => ({
    default: m.DevRealBlocksPage,
  }))
);
const InlineCoursePage = lazy(() =>
  import("./pages/inline-course").then((m) => ({ default: m.InlineCoursePage }))
);
const InlineCourseLayout = lazy(() =>
  import("./pages/inline-course").then((m) => ({
    default: m.InlineCourseLayout,
  }))
);
const ScormPage = lazy(() =>
  import("./pages/scorm").then((m) => ({ default: m.ScormPage }))
);
const ScormLayout = lazy(() =>
  import("./pages/scorm").then((m) => ({ default: m.ScormLayout }))
);

const EmbedAgentPage = lazy(() =>
  import("./pages/embed-agent").then((m) => ({ default: m.EmbedAgentPage }))
);

// Wrapper для lazy компонентов с Suspense
const LazyPage = ({
  children,
  fallbackText = "Загрузка...",
}: {
  children: React.ReactNode;
  fallbackText?: string;
}) => (
  <Suspense fallback={<PageSpinner text={fallbackText} />}>{children}</Suspense>
);

// Wrapper компонент для SimulatorPage с параметрами
const SimulatorPageWrapper = () => {
  // Получаем параметры из URL через window.location
  // В Data Mode используем хук useParams внутри компонента
  return (
    <LazyPage fallbackText="Загружаем симулятор...">
      <SimulatorPageWithParams />
    </LazyPage>
  );
};

// Отдельный компонент для получения параметров
import { useParams } from "react-router";

const SimulatorPageWithParams = () => {
  const { lessonId, simulatorId } = useParams<{
    lessonId: string;
    simulatorId: string;
  }>();

  if (!lessonId || !simulatorId) {
    return <PageSpinner text="Ошибка: параметры не найдены" />;
  }

  return <SimulatorPage lessonId={lessonId} simulatorId={simulatorId} />;
};

// Error Boundary компонент
const ErrorBoundary = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Что-то пошло не так
        </h1>
        <p className="text-gray-600 mb-6">
          Произошла ошибка при загрузке страницы
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
        >
          Перезагрузить страницу
        </button>
      </div>
    </div>
  );
};

// Not Found компонент
const NotFound = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Страница не найдена
        </h2>
        <p className="text-gray-600 mb-6">
          Запрашиваемая страница не существует
        </p>
        <a
          href="/"
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity inline-block"
        >
          На главную
        </a>
      </div>
    </div>
  );
};

// Проверка, является ли текущий хост inline курсом
const isInlineCoursesHost = () => {
  const inlineCoursesHost = import.meta.env.VITE_INLINE_COURSES_HOST;
  if (!inlineCoursesHost) return false;

  const currentHost = window.location.host;
  return currentHost === inlineCoursesHost;
};

// Embed ИИ-блока для SCORM-пакетов: вне Root, без редиректов на логин
const embedRoute = {
  path: "/embed/agent/:blockId",
  element: (
    <LazyPage fallbackText="Загрузка упражнения...">
      <EmbedAgentPage />
    </LazyPage>
  ),
  ErrorBoundary: ErrorBoundary,
};

// Роуты для inline курсов
const inlineCoursesRoutes = [
  embedRoute,
  {
    path: "/scorm",
    element: (
      <LazyPage fallbackText="Подготовка...">
        <ScormLayout />
      </LazyPage>
    ),
    ErrorBoundary: ErrorBoundary,
    children: [
      {
        path: ":courseSlug",
        element: (
          <LazyPage fallbackText="Загружаем курс...">
            <ScormPage />
          </LazyPage>
        ),
      },
      {
        path: "*",
        Component: NotFound,
      },
    ],
  },
  {
    path: "/",
    Component: InlineCourseLayout,
    ErrorBoundary: ErrorBoundary,
    children: [
      {
        path: ":courseSlug",
        element: (
          <LazyPage fallbackText="Загружаем курс...">
            <InlineCoursePage />
          </LazyPage>
        ),
      },
      {
        path: "*",
        Component: NotFound,
      },
    ],
  },
];

// Стандартные роуты
const standardRoutes = [
  embedRoute,
  // SCORM роуты - отдельный layout, не вложен в Root
  // Основные роуты приложения
  {
    path: "/",
    Component: Root,
    ErrorBoundary: ErrorBoundary,
    children: [
      {
        index: true,
        element: (
          <LazyPage fallbackText="Загружаем курс...">
            <HomePage />
          </LazyPage>
        ),
      },
      {
        path: "login",
        element: (
          <LazyPage fallbackText="Загрузка...">
            <LoginPage />
          </LazyPage>
        ),
      },
      {
        path: "register",
        element: (
          <LazyPage fallbackText="Загрузка...">
            <RegisterPage />
          </LazyPage>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <LazyPage fallbackText="Загрузка...">
            <ForgotPasswordPage />
          </LazyPage>
        ),
      },
      {
        // Установка пароля по ссылке из письма после оплаты (публичный, ssl_id/sso-курсы)
        path: "set_password",
        element: (
          <LazyPage fallbackText="Загрузка...">
            <SetPasswordPage />
          </LazyPage>
        ),
      },
      {
        path: "profile",
        element: (
          <LazyPage fallbackText="Загрузка профиля...">
            <ProfilePage />
          </LazyPage>
        ),
      },
      {
        path: "character",
        element: (
          <LazyPage fallbackText="Загрузка...">
            <CharacterPage />
          </LazyPage>
        ),
      },
      {
        path: "payment",
        element: (
          <LazyPage fallbackText="Загрузка...">
            <PaymentPage />
          </LazyPage>
        ),
      },
      ...(import.meta.env.DEV
        ? [
            {
              path: "dev/real-blocks",
              element: (
                <LazyPage fallbackText="Загрузка реальных блоков...">
                  <DevRealBlocksPage />
                </LazyPage>
              ),
            },
          ]
        : []),
      {
        path: "lesson/:lessonId/simulator/:simulatorId",
        Component: SimulatorPageWrapper,
      },
      {
        path: "*",
        Component: NotFound,
      },
    ],
  },
];

// Выбираем роуты в зависимости от хоста
export const router = createBrowserRouter(
  isInlineCoursesHost() ? inlineCoursesRoutes : standardRoutes
);
